import {useDispatch, useSelector} from "react-redux";

import {useToolBuilder} from "./ToolBuilderHooks";
import {useEffect} from "react";

import {service as ContextNodeService} from "../Services/ContextNodeService";

import {SET_BREADCRUMBS, SET_CONTEXT} from "../Redux/Actions/Types";
import {selectCurrentUser} from "../Redux/Selectors/userSelectors";

export const useContextBuilder = (props) => {
    const currentUser = useSelector(selectCurrentUser);
    const dispatch = useDispatch();
    const { params } = props?.match;


    return () => {
        let lookupParams = [];

        if(params) {
            if(params.pProgId && params.pProgId != 'new') {
                lookupParams['programId'] = params.pProgId;
            }

            if(params.cProgId && params.cProgId != 'new') {
                lookupParams['subProgramId'] = params.cProgId;
            }

            if(params.projId && params.projId != 'new') {
                lookupParams['projectId'] = params.projId;
            }

            if(params.conActId && params.conActId != 'new') {
                lookupParams['conservationActionId'] = params.conActId;
            }

            if(params.sampleEventId && params.sampleEventId != 'new') {
                lookupParams['sampleEventId'] = params.sampleEventId;
            }

            Promise.all([ContextNodeService.Lookup(lookupParams)])
                .then(([result]) => {
                    const { program, subProgram, project, conservationAction, sampleEvent = null } = result;

                    //TODO: Use StreamBankContext class. (This will need to encode / decode to json correctly).
                    let context = {
                        sampleEvent: null,
                        conservationAction: null,
                        project: null,
                        childProgram: null,
                        parentProgram: null,
                        organization: null,
                    };

                    let contextNodeId = null;
                    let contextNodeTypeId = null;

                    let breadcrumbs = [];

                    if(sampleEvent) {
                        breadcrumbs.unshift({
                            title: sampleEvent.name,
                            shortTitle: sampleEvent.shortName,
                            context: {
                                sampleEvent: sampleEvent.sampleEventId,
                                conservationAction: conservationAction.conservationActionId,
                                project: (project.slug || project.projectId),
                                childProgram: (subProgram.slug || subProgram.programId),
                                parentProgram: (program.slug || program.programId)
                            }
                        });
                    } else if (params?.conActId === 'new') {
                        context["conservationAction"] = "new";
                    }
                    if(conservationAction) {
                        contextNodeId = contextNodeId || conservationAction.contextNodeId;
                        contextNodeTypeId = contextNodeTypeId ?? 4;

                        context["conservationAction"] = conservationAction.conservationActionId;
                        context["conservationActionContextNodeId"] = conservationAction.contextNodeId;

                        let conservationActionName = conservationAction.ConservationActionType.name;

                        breadcrumbs.unshift({
                            title: project.name + " " + conservationActionName,
                            shortTitle: (project.shortName != null ? project.shortName : project.name) + " " + conservationActionName,
                            context: {
                                conservationAction: conservationAction.conservationActionId,
                                project: (project.slug || project.projectId),
                                childProgram: (subProgram.slug || subProgram.programId),
                                parentProgram: (program.slug || program.programId)
                            }
                        });
                    } else if (params?.conActId === 'new') {
                        context["conservationAction"] = "new";
                        // TODO: do I need to unset conservationActionContextNodeId ?
                    }
                    if(project) {
                        contextNodeId = contextNodeId || project.contextNodeId;
                        contextNodeTypeId = contextNodeTypeId ?? 3;

                        context["project"] = (project.slug || project.projectId);
                        context["projectContextNodeId"] = project.contextNodeId;

                        breadcrumbs.unshift({
                            title: project.name,
                            shortTitle: project.shortName,
                            context: {
                                conservationAction: null,
                                project: (project.slug || project.projectId),
                                childProgram: (subProgram.slug || subProgram.programId),
                                parentProgram: (program.slug || program.programId)
                            }
                        });
                    } else if (params?.projId === 'new') {
                        context["project"] = "new";
                    }
                    if(subProgram) {
                        contextNodeId = contextNodeId || subProgram.contextNodeId;
                        contextNodeTypeId = contextNodeTypeId ?? 2;

                        context["childProgram"] = (subProgram.slug || subProgram.programId);
                        context["childProgramContextNodeId"] = subProgram.contextNodeId;

                        breadcrumbs.unshift({
                            title: subProgram.name,
                            shortTitle: subProgram.shortName,
                            context: {
                                conservationAction: null,
                                project: params.projId != 'new' ? null: 'new',
                                childProgram: (subProgram.slug || subProgram.programId),
                                parentProgram: (program.slug || program.programId)
                            }
                        });
                    } else if (params?.cProgId === 'new') {
                        context["childProgram"] = "new";
                    }
                    if(program) {
                        contextNodeId = contextNodeId || program.contextNodeId;
                        contextNodeTypeId = contextNodeTypeId ?? 1;

                        context["parentProgram"] = (program.slug || program.programId);
                        context["programContextNodeId"] = program.contextNodeId;
                        
                        context["organization"] = program.Organization.code;
                        context["organizationId"] = program.Organization.organizationId;

                        breadcrumbs.unshift({
                            title: program.name,
                            shortTitle: program.shortName,
                            context: {
                                conservationAction: null,
                                project: null,
                                childProgram: params.cProgId != 'new' ? null: 'new',
                                parentProgram: (program.slug || program.programId)
                            }
                        });
                    } else if (params?.pProgId === 'new') {
                        context["parentProgram"] = "new";
                    }
                    
                    if(!context['organizationId']) {
                        context['organizationId'] = currentUser?.organizationId;
                    }

                    context["contextNodeId"] = contextNodeId;
                    context["contextNodeTypeId"] = contextNodeTypeId;

                    dispatch({type: SET_BREADCRUMBS, breadcrumbs});
                    dispatch({type: SET_CONTEXT, context});
                })
                .catch(() => {
                    //TODO: handle error context
                });
        }
    }
};


/**
 * This hook wraps pages that do not have a standard context/tool url route
 * All of the page components that it wraps will get reloaded
 * 1. Get default loadData and shouldReloadData based on the context and
 *    tool specified by the "match" prop
 * NOTE: I think it would make sense to combine this with the StreamBankToolRouter ?
 */

export const useContextIndependentPage = (props) => {
    const {params} = props.match;
    const {tool, pProgId, cProgId, projId, conActId, sampleEventId} = params;

    const contextBuilder = useContextBuilder(props);
    const toolBuilder = useToolBuilder(props);

    useEffect(() => {
        contextBuilder();
        toolBuilder();
    }, [tool, pProgId, cProgId, projId, conActId, sampleEventId]);
};
